import type { PropsWithChildren } from 'react'
import React from 'react'
import renderHTML from 'react-html-parser'
import clsx from 'clsx'
import { useFeature } from 'toggled'

import { useProductStock } from '~/hooks'
import VariantsStock from '~/screens/product/variants-stock'
import theme from '~/truck/theme'
import type { IProduct } from '~/types/products'
import { flags } from '~/utils/constants'

import { Container } from './cta-basic-content.style'

export function CTABasicContent(
  props: PropsWithChildren<{
    activeVariant?: IProduct
    name?: string
    presentation?: string
    sku?: string
    variants?: IProduct[]
    fallbackProduct?: IProduct
  }>,
) {
  const { activeVariant, presentation, name, children, sku, variants, fallbackProduct } = props

  const showSkuLabelFeature = useFeature(flags.SHOW_SKU_LABEL)

  const hasVariants = fallbackProduct?.hasVariants && variants && variants.length > 0

  const { canPurchase, shouldShow, displayValue, showVariantsTable, variantsLabel } = useProductStock({
    product: {
      stock: activeVariant?.stock ?? 0,
      restockDate: activeVariant?.restockDate,
      jumpFactor: activeVariant?.jumpFactor,
      minimumSalesQuantity: activeVariant?.minimumSalesQuantity,
      maximumSalesQuantity: activeVariant?.maximumSalesQuantity,
    },
    context: { hasVariants },
  })

  return (
    <Container style={{ gap: theme.spacing.compact * 3 }}>
      <h1 className="text-xl font-semibold">{renderHTML(name)}</h1>
      <Container style={{ gap: theme.spacing.compact }}>
        <h3 className={clsx('text-secondary', activeVariant ? 'visible' : 'invisible')}>{presentation}</h3>
        {showSkuLabelFeature ? (
          <h3 className={clsx('text-secondary', activeVariant ? 'visible' : 'invisible')}>SKU: {sku}</h3>
        ) : null}
      </Container>
      {shouldShow ? (
        <div className="flex items-baseline gap-2">
          {canPurchase ? (
            <p className="text-green-600">
              <span className="font-semibold">{displayValue}</span> en stock
            </p>
          ) : (
            <p className="font-semibold text-red-500">Sin stock</p>
          )}
          {showVariantsTable && (
            <VariantsStock variants={variants} activeVariant={activeVariant} variantsLabel={variantsLabel} />
          )}
        </div>
      ) : null}
      {children}
    </Container>
  )
}
